import type { StaticImageData } from "next/image";

import Image from "next/image";
import { m } from "motion/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { IconCheck } from "~/common/components/icons/check";
import { useWorkspace } from "~/workspaces/hooks/use-workspace";
import { useGetWorkspaces } from "~/workspaces/queries/get-workspaces-query";
import { IconChevronUpDown } from "~/common/components/icons/chevron-up-down";
import { TextIcon } from "~/common/components/text-icon";
import { useAppTheme } from "~/common/hooks/dom/use-app-theme";

import { dropdownStyle } from "./workspace-select-dropdown.style";

type WorkspaceIconProps = {
  title: string;
  icon: StaticImageData | null;
};

type WorkspaceSelectDropdownProps = {
  dataTestTag?: string;
};

function WorkspaceIcon({ title, icon }: WorkspaceIconProps) {
  return icon ? (
    <Image
      src={icon}
      alt="workspace logo"
      height={20}
      width={20}
      aria-hidden
      priority
      loading="eager"
      className={dropdownStyle.image()}
    />
  ) : (
    <TextIcon text={title} />
  );
}

function WorkspaceSelectDropdown({
  dataTestTag,
}: WorkspaceSelectDropdownProps) {
  const { selectedWorkspace, setSelectedWorkspace } = useWorkspace();
  const { data: workspaces } = useGetWorkspaces();

  const appTheme = useAppTheme();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={dropdownStyle.triggerButton()}
          data-test={dataTestTag ?? "dropdown"}
        >
          <div className={dropdownStyle.triggerButtonContent()}>
            <WorkspaceIcon
              title={selectedWorkspace.title}
              icon={selectedWorkspace.icon}
            />
            <span>{selectedWorkspace.title}</span>
          </div>
          <IconChevronUpDown />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          asChild
          sideOffset={4}
        >
          <m.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={dropdownStyle.dropdownContainer({
              theme: appTheme as "light",
            })}
          >
            {workspaces.map((workspace) => {
              return (
                <DropdownMenu.Item
                  key={workspace.id}
                  onSelect={() => setSelectedWorkspace(workspace)}
                  className={dropdownStyle.dropdownItem()}
                  disabled={false} // TODO: Add disabled workspaces logic
                  data-test={`menu-item-${workspace.title}`}
                >
                  <div className={dropdownStyle.workspaceInfoWrapper()}>
                    <WorkspaceIcon
                      title={workspace.title}
                      icon={workspace.icon}
                    />
                    {workspace.title}
                  </div>
                  <span className="size-20">
                    {selectedWorkspace?.id === workspace.id ? (
                      <IconCheck />
                    ) : (
                      ""
                    )}
                  </span>
                </DropdownMenu.Item>
              );
            })}

            <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
          </m.div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export { WorkspaceSelectDropdown };
